// ABOUTME: Renders Applicant WhatsApp media actions from the public conversation DTO.
// ABOUTME: Keeps contact-episode storage and provider identifiers out of frontend decisions.
import React from "react";
import { Box, Button, Chip } from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import { canOpenWhatsappMedia } from "Pages/WhatsappInbox/helpers";
import {
    canSaveWhatsappContactEpisodeMedia,
    WhatsappContactEpisodeMode
} from "functions/whatsappContactEpisode";

interface ApplicantWhatsappMediaActionLabels {
    download: string;
    saveAsDocument: string;
    saveAsCv: string;
    savedToApplicant: string;
}

interface ApplicantWhatsappMediaActionsProps {
    message: Compleo.IObject;
    mode: WhatsappContactEpisodeMode;
    canMutate: boolean;
    messageActionId: string;
    labels: ApplicantWhatsappMediaActionLabels;
    className?: string;
    onDownload: (message: Compleo.IObject) => void;
    onSave: (message: Compleo.IObject, saveAs: "document" | "cv") => void;
}

const isContactEpisodeMessage = (message: Compleo.IObject) =>
    String(message.scope || "") === "CONTACT_EPISODE" ||
    String(message.conversationReference?.scope || "") === "CONTACT_EPISODE" ||
    Boolean(String(message.contactEpisodeId || "").trim());

export const ApplicantWhatsappMediaActions = ({
    message,
    mode,
    canMutate,
    messageActionId,
    labels,
    className,
    onDownload,
    onSave
}: ApplicantWhatsappMediaActionsProps) => {
    const contactEpisodeMessage = isContactEpisodeMessage(message);
    const canDownload = contactEpisodeMessage
        ? message.hasMedia === true
        : Boolean(message.mediaS3Key) || canOpenWhatsappMedia(message);
    const mediaSavedToApplicant = contactEpisodeMessage
        ? message.mediaSavedToApplicant === true
        : Boolean(message.persistentMediaS3Key);
    const hasSavableMedia = contactEpisodeMessage
        ? message.canSaveMediaToApplicant === true
        : Boolean(message.mediaId);
    const canSave =
        canMutate &&
        message.direction !== "OUTGOING" &&
        hasSavableMedia &&
        !mediaSavedToApplicant &&
        canSaveWhatsappContactEpisodeMedia(mode, message);

    if (!canDownload && !canSave && !mediaSavedToApplicant) {
        return null;
    }

    return (
        <Box className={className}>
            {canDownload && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    disabled={
                        messageActionId === `${message.messageId}:download`
                    }
                    onClick={() => onDownload(message)}
                >
                    {labels.download}
                </Button>
            )}
            {canSave && (
                <>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<DescriptionIcon />}
                        disabled={
                            messageActionId === `${message.messageId}:document`
                        }
                        onClick={() => onSave(message, "document")}
                    >
                        {labels.saveAsDocument}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<SaveAltIcon />}
                        disabled={messageActionId === `${message.messageId}:cv`}
                        onClick={() => onSave(message, "cv")}
                    >
                        {labels.saveAsCv}
                    </Button>
                </>
            )}
            {mediaSavedToApplicant && (
                <Chip
                    size="small"
                    label={labels.savedToApplicant}
                    color="primary"
                />
            )}
        </Box>
    );
};

export default ApplicantWhatsappMediaActions;
